import React from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { StatusPill, AnalyzingBadge } from "@/components/StatusPill";
import { useOrdersPolling } from "@/hooks/useOrdersPolling";
import { useAuth } from "@/context/AuthContext";
import { ReferralCard } from "@/components/ReferralCard";
import { PushPromptBanner } from "@/components/PushPromptBanner";
import { UploadCloud, Coins, FileStack, Clock, ArrowRight, Cpu } from "lucide-react";
import { useI18n } from "@/i18n";

export const ClientDashboard = () => {
  const { user } = useAuth();
  const { orders, loading } = useOrdersPolling();
  const { t } = useI18n();

  const inProgress = orders.filter((o) => o.status === "in_progress").length;
  const completed = orders.filter((o) => o.status === "completed").length;

  const stats = [
    { icon: Coins, label: t("dash.statCredits"), value: user?.credits ?? 0, color: "#00E5FF" },
    { icon: FileStack, label: t("dash.statFiles"), value: orders.length, color: "#8B5CF6" },
    { icon: Clock, label: t("dash.statProcessing"), value: inProgress, color: "#FFB020" },
    { icon: Cpu, label: t("dash.statDone"), value: completed, color: "#00E5A0" },
  ];

  return (
    <AppShell mode="client">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-2">{t("dash.welcome")}</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{t("dash.hello")}, {user?.name?.split(" ")[0]}</h1>
      </div>

      <PushPromptBanner />

      {/* CTA upload */}
      <Link to="/client/new" data-testid="dashboard-new-order" className="group block glass rounded-md p-8 mb-8 relative overflow-hidden hover:border-aurora-cyan/40 transition-all hover:shadow-glow">
        <div className="absolute -right-10 -top-10 w-48 h-48 bg-aurora-cyan/10 blur-3xl rounded-full" />
        <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 grid place-items-center border border-dashed border-aurora-cyan/40 rounded-md bg-aurora-cyan/5 group-hover:bg-aurora-cyan/10 transition-colors">
              <UploadCloud className="w-8 h-8 text-aurora-cyan" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t("dash.ctaTitle")}</h2>
              <p className="text-zinc-400 text-sm mt-1">{t("dash.ctaSub")}</p>
            </div>
          </div>
          <ArrowRight className="w-6 h-6 text-aurora-cyan group-hover:translate-x-2 transition-transform" />
        </div>
      </Link>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="glass rounded-md p-5">
              <Icon className="w-5 h-5 mb-3" style={{ color: s.color }} />
              <div className="font-display text-3xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Referral */}
      <ReferralCard />

      {/* Recent orders */}
      <div className="glass rounded-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-semibold uppercase tracking-tight">{t("dash.recent")}</h3>
          <Link to="/client/orders" className="font-mono text-xs text-aurora-cyan uppercase tracking-widest hover:underline">{t("dash.viewAll")}</Link>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 py-8 text-center">{t("dash.loading")}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <FileStack className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">{t("dash.noOrders")}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {orders.slice(0, 5).map((o) => (
              <Link key={o.id} to={`/client/orders/${o.id}`} data-testid={`order-row-${o.id}`} className="flex items-center justify-between py-3 px-3 rounded-sm hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                <div className="min-w-0">
                  <div className="font-display uppercase tracking-tight truncate">{o.vehicle_brand} {o.vehicle_model}</div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">{o.original_filename} · {o.services.length} {t("dash.solutions")} · {o.total_credits} cr.</div>
                </div>
                {o.analyzing ? <AnalyzingBadge /> : <StatusPill status={o.status} />}
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
};
